// CB:BEGIN admin-support-requests-page
import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

interface SupportRequest {
  id: string;
  actorUserId: string | null;
  projectId: string | null;
  payload: Record<string, unknown> | null;
  submittedAt: string;
}

interface Props {
  onNavigate?: (screen: string) => void;
}

const AdminSupportRequestsPage: React.FC<Props> = () => {
  const { getToken } = useAuth();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  useEffect(() => {
    fetchRequests();
  }, [roleFilter, offset]);

  async function fetchRequests() {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (roleFilter) params.set('role', roleFilter);
      const res = await fetch(`/api/admin/support/requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRequests(data.requests ?? []);
      setTotal(data.total ?? 0);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load support requests');
    } finally {
      setLoading(false);
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString();
  }

  function getSubject(payload: Record<string, unknown> | null): string {
    return (payload?.subject as string) || '(no subject)';
  }

  function getMessage(payload: Record<string, unknown> | null): string {
    return (payload?.message as string) || '';
  }

  function getRoleLabel(payload: Record<string, unknown> | null): string {
    return (payload?.role as string) || '—';
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Support Requests</h1>
        <p className="text-sm text-gray-500 mt-1">
          User-submitted help requests ({total} total)
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select
          value={roleFilter}
          onChange={e => { setRoleFilter(e.target.value); setOffset(0); }}
          className="border rounded px-3 py-1.5 text-sm"
        >
          <option value="">All roles</option>
          <option value="homeowner">Homeowner</option>
          <option value="contractor">Contractor</option>
          <option value="property_manager">Property Manager</option>
          <option value="admin">Admin</option>
        </select>
        <button
          onClick={() => fetchRequests()}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-gray-400 border rounded-lg bg-white">
          No support requests found.
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(req => (
            <div key={req.id} className="bg-white border rounded-lg p-4 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{getSubject(req.payload)}</p>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{getMessage(req.payload)}</p>
                </div>
                <div className="text-right text-xs text-gray-400 shrink-0">
                  <p>{formatDate(req.submittedAt)}</p>
                  <span className="mt-1 inline-block bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {getRoleLabel(req.payload)}
                  </span>
                </div>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-gray-400">
                <span>User ID: {req.actorUserId ?? '—'}</span>
                {req.projectId && <span>Project: {req.projectId}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex gap-3 mt-6 justify-center items-center text-sm">
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
            className="px-3 py-1.5 border rounded disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-gray-500">
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <button
            disabled={offset + limit >= total}
            onClick={() => setOffset(offset + limit)}
            className="px-3 py-1.5 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminSupportRequestsPage;
// CB:END admin-support-requests-page
